import React, { useEffect, useMemo, useRef, useState } from "react"
import { EditIcon, MicIcon, VideoIcon, VideoOffIcon } from "./Icons"
import { ArrowPathIcon } from "@heroicons/react/24/outline"
import { useI18n } from "../i18n"
import { useFaceBlurOverlay } from "../hooks/useFaceBlurOverlay"

function isSecureEnoughForCamera() {
  return window.isSecureContext || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
}

function useStatusMessage(status) {
  const { t } = useI18n()

  switch (status) {
    case "unsupported": return t("camera.status.unsupported")
    case "insecure": return t("camera.status.insecure")
    case "denied": return t("camera.status.denied")
    case "error": return t("camera.status.error")
    case "loading": return t("camera.status.loading")
    case "off": return t("camera.status.off")
    case "idle": return t("camera.status.idle")
    default: return t("camera.status.loading")
  }
}

function initialsFor(label) {
  const trimmed = label.trim()
  if (/^\p{Emoji}/u.test(trimmed)) return trimmed
  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() || "")
    .join("") || "Y"
}

export function MediaToggle({ active, label, onClick, children }) {
  return (
    <button
      className={`yano-media-toggle ${active ? "" : "yano-media-toggle--off"}`.trim()}
      type="button"
      onClick={onClick}
      aria-pressed={!active}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  )
}

export function CameraPreview({
  className = "",
  label = "",
  mirrored = true,
  minimal = false,
  vignette = false,
  faceBlur = false,
  canEditLabel = false,
  editingLabel = false,
  draftLabel = "",
  onDraftLabelChange,
  onEditLabel,
  onSaveLabel,
  onCancelLabel,
  videoEnabled = true,
  audioEnabled = true,
  onToggleVideo,
  onToggleAudio
}) {
  const videoRef = useRef(null)
  const faceBlurCanvasRef = useRef(null)
  const streamRef = useRef(null)
  const audioTrackRef = useRef(null)
  const [permissionRequested, setPermissionRequested] = useState(false)
  const [status, setStatus] = useState(videoEnabled ? "idle" : "off")
  const [errorDetail, setErrorDetail] = useState("")
  const [attempt, setAttempt] = useState(0)
  const [facingMode, setFacingMode] = useState("user")
  const [readyFlash, setReadyFlash] = useState(false)
  const lastReadyRef = useRef(false)
  const { t } = useI18n()
  const statusMessage = useStatusMessage(status)

  useEffect(() => {
    let cancelled = false

    async function startPreview() {
      setErrorDetail("")

      if (!videoEnabled) {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }
        audioTrackRef.current = null

        if (videoRef.current) {
          videoRef.current.srcObject = null
        }

        setStatus("off")
        return
      }

      if (!permissionRequested) {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }
        audioTrackRef.current = null

        if (videoRef.current) {
          videoRef.current.srcObject = null
        }

        setStatus("idle")
        return
      }

      setStatus("loading")

      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("unsupported")
        return
      }

      if (!isSecureEnoughForCamera()) {
        setStatus("insecure")
        return
      }

      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }
        audioTrackRef.current = null

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode }
          },
          // If the mic toggle is on, request mic permission together with camera so we don't
          // trigger a second permission prompt when the call provider asks for audio.
          audio: Boolean(audioEnabled)
        })

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream
        audioTrackRef.current = stream.getAudioTracks()[0] || null

        if (!videoRef.current) {
          throw new Error(t("camera.status.attach_failed"))
        }

        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {})
        setStatus("ready")
      } catch (error) {
        const nextStatus = error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError" ? "denied" : "error"
        setStatus(nextStatus)
        setErrorDetail(error?.message || "")
      }
    }

    startPreview()

	    return () => {
	      cancelled = true
	      if (streamRef.current) {
	        streamRef.current.getTracks().forEach((track) => track.stop())
	        streamRef.current = null
	      }
        audioTrackRef.current = null
	    }
	  }, [attempt, videoEnabled, facingMode, permissionRequested])

  useEffect(() => {
    let active = true

    async function syncAudioTrack() {
      if (!videoEnabled) return
      if (status !== "ready") return
      if (!streamRef.current) return

      const stream = streamRef.current

      if (!audioEnabled) {
        const track = audioTrackRef.current
        if (track) {
          track.enabled = false
          try { track.stop() } catch (_) {}
        }
        audioTrackRef.current = null
        stream.getAudioTracks().forEach((t) => stream.removeTrack(t))
        return
      }

      // Ensure we have an audio track without restarting the camera preview.
      if (audioTrackRef.current && audioTrackRef.current.readyState !== "ended") {
        audioTrackRef.current.enabled = true
        return
      }

      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        if (!active) {
          audioStream.getTracks().forEach((t) => t.stop())
          return
        }
        const track = audioStream.getAudioTracks()[0] || null
        if (!track) {
          audioStream.getTracks().forEach((t) => t.stop())
          return
        }

        stream.addTrack(track)
        audioTrackRef.current = track
        audioStream.getTracks().forEach((t) => t !== track && t.stop())
      } catch (_error) {
        // Ignore; camera preview should still work.
      }
    }

    syncAudioTrack()

    return () => {
      active = false
    }
  }, [audioEnabled, status, videoEnabled])

  const ready = status === "ready"
  const shouldMirror = mirrored && facingMode === "user"
  const initials = useMemo(() => initialsFor(label), [label])
  const showPlaceholder = !videoEnabled || !ready

  useFaceBlurOverlay({
    enabled: Boolean(faceBlur && !showPlaceholder),
    sourceVideoRef: videoRef,
    overlayRef: faceBlurCanvasRef,
    holdMs: 1200,
    lerpA: 0.22,
    fit: "cover"
  })

  useEffect(() => {
    const lastReady = lastReadyRef.current
    lastReadyRef.current = ready

    if (!videoEnabled) return
    if (ready && !lastReady) {
      setReadyFlash(true)
      const timeout = window.setTimeout(() => setReadyFlash(false), 220)
      return () => window.clearTimeout(timeout)
    }
  }, [ready, videoEnabled])

  return (
    <div className={`relative overflow-hidden rounded-[calc(2rem-2px)] bg-[#130a22] ${className}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`yano-camera-video h-full w-full object-cover ${shouldMirror ? "scale-x-[-1]" : ""} ${showPlaceholder ? "hidden" : "block"}`}
      />

      {faceBlur && !showPlaceholder ? (
        <canvas
          ref={faceBlurCanvasRef}
          className={`yano-camera-faceblur yano-camera-canvas pointer-events-none absolute inset-0 h-full w-full ${shouldMirror ? "scale-x-[-1]" : ""}`}
        />
      ) : null}

      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 yano-preview-vignette ${vignette && !showPlaceholder ? "yano-preview-vignette--on" : ""}`}
      />

      {!showPlaceholder ? (
        <button
          type="button"
          className="absolute bottom-3 left-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white/80 backdrop-blur-sm transition hover:bg-black/55 hover:text-white active:scale-90"
          onClick={() => setFacingMode((current) => current === "user" ? "environment" : "user")}
          aria-label={t("camera.flip")}
        >
          <ArrowPathIcon className="h-4.5 w-4.5" />
        </button>
      ) : null}

      {showPlaceholder ? (
        <div className="flex h-full min-h-0 items-center justify-center bg-[radial-gradient(circle_at_50%_30%,rgba(139,92,246,0.18),transparent_60%)] p-4 text-center text-violet-200">
          <div className="max-w-sm">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full border-2 border-violet-400/20 bg-violet-500/12 text-2xl font-extrabold text-violet-300">
              {videoEnabled ? (status === "idle" ? <VideoIcon /> : initials) : <VideoOffIcon />}
            </div>
            <p className="mt-3 font-display text-lg leading-none text-violet-200 sm:text-xl">
              {videoEnabled ? (status === "idle" ? t("camera.idle.title") : t("camera.check")) : t("camera.off")}
            </p>
            {videoEnabled && status === "idle" ? (
              <p className="mt-1.5 text-xs font-semibold text-violet-400/50 sm:text-sm">{t("camera.idle.hint")}</p>
            ) : videoEnabled ? (
              <p className="mt-1.5 text-xs font-semibold text-violet-400/50 sm:text-sm">{statusMessage}</p>
            ) : null}
            {errorDetail ? <p className="mt-1.5 text-xs text-violet-400/40">{errorDetail}</p> : null}
            {videoEnabled && status === "idle" ? (
              <button
                className="mt-3 rounded-xl border-2 border-violet-400/30 bg-violet-500/25 px-4 py-2.5 text-sm font-extrabold text-violet-100 transition hover:bg-violet-500/40"
                type="button"
                onClick={() => setPermissionRequested(true)}
              >
                {t("camera.idle.button")}
              </button>
            ) : videoEnabled && !ready ? (
              <button className="mt-3 rounded-xl border-2 border-violet-500/20 bg-violet-500/12 px-4 py-2.5 text-sm font-extrabold text-violet-200 transition hover:bg-violet-500/20" type="button" onClick={() => setAttempt((value) => value + 1)}>
                {t("camera.retry")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={`absolute top-4 flex items-start gap-3 ${minimal ? "right-4" : "inset-x-4 justify-between"}`}>
        {!minimal ? (
          <>
            {editingLabel ? (
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full bg-black/40 px-3 py-2 text-sm text-white backdrop-blur-sm">
                <input
                  type="text"
                  value={draftLabel}
                  onChange={(event) => onDraftLabelChange?.(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onSaveLabel?.()
                    if (event.key === "Escape") onCancelLabel?.()
                  }}
                  placeholder={t("camera.guest_name_placeholder")}
                  className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/45"
                  autoFocus
                />
                <button className="rounded-full bg-white/14 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20" type="button" onClick={onSaveLabel}>
                  {t("camera.save_name")}
                </button>
              </div>
            ) : (
              <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-black/40 px-3 py-2 text-sm text-white backdrop-blur-sm">
                <span className="truncate">{label}</span>
                {canEditLabel ? (
                  <button
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/14 text-white transition hover:bg-white/20"
                    type="button"
                    onClick={onEditLabel}
                    aria-label={t("camera.edit_name")}
                  >
                    <EditIcon />
                  </button>
                ) : null}
              </div>
            )}
          </>
        ) : null}

        <div className={`inline-flex items-center gap-2 rounded-full bg-black/40 px-3 py-2 font-semibold text-white/90 backdrop-blur-sm ${minimal ? "text-[11px]" : "text-xs"}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${readyFlash ? "yano-dot-pop" : ""} ${videoEnabled && ready ? "bg-[var(--lime)]" : "bg-white/45"}`} />
          {videoEnabled ? (ready ? t("camera.ready") : statusMessage) : t("camera.off")}
        </div>
      </div>

      {!minimal ? (
        <div className="absolute inset-x-0 bottom-4 flex justify-center px-4">
          <div className="inline-flex items-center gap-3 text-white">
            <MediaToggle active={audioEnabled} label={audioEnabled ? t("mic.on") : t("mic.off")} onClick={onToggleAudio}>
              <MicIcon />
            </MediaToggle>
            <MediaToggle active={videoEnabled} label={videoEnabled ? t("video.on") : t("video.off")} onClick={onToggleVideo}>
              <VideoIcon />
            </MediaToggle>
          </div>
        </div>
      ) : null}
    </div>
  )
}
